import React from "react";
import BatteryAlertIcon from "@mui/icons-material/BatteryAlert";
import Battery20Icon from "@mui/icons-material/Battery20";
import Battery30Icon from "@mui/icons-material/Battery30";
import Battery50Icon from "@mui/icons-material/Battery50";
import Battery60Icon from "@mui/icons-material/Battery60";
import Battery80Icon from "@mui/icons-material/Battery80";
import Battery90Icon from "@mui/icons-material/Battery90";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";

import BatteryCharging20Icon from "@mui/icons-material/BatteryCharging20";
import BatteryCharging30Icon from "@mui/icons-material/BatteryCharging30";
import BatteryCharging50Icon from "@mui/icons-material/BatteryCharging50";
import BatteryCharging60Icon from "@mui/icons-material/BatteryCharging60";
import BatteryCharging80Icon from "@mui/icons-material/BatteryCharging80";
import BatteryCharging90Icon from "@mui/icons-material/BatteryCharging90";
import BatteryChargingFullIcon from "@mui/icons-material/BatteryChargingFull";

import BatteryUnknownIcon from "@mui/icons-material/BatteryUnknown";

import { amber } from "@mui/material/colors";
import { green } from "@mui/material/colors";

const HIGH_BATTERY_THRESHOLD = 85;

const BatteryIcon: React.FC<{
  batteryPercentage?: number | null;
  isCharging?: boolean;
}> = ({ batteryPercentage, isCharging = false }) => {
  if (batteryPercentage == null || Number.isNaN(batteryPercentage)) {
    return null;
  }

  const p = Math.min(100, Math.max(0, Math.round(batteryPercentage)));

  let color: string | undefined = undefined;
  if (batteryPercentage >= HIGH_BATTERY_THRESHOLD) {
    color = green[500];
  } else if (isCharging) {
    color = amber[500];
  }

  const steps: {
    max: number;
    stdIcon: React.ElementType;
    chargingIcon: React.ElementType;
    color: string;
  }[] = [
    { max: 10, stdIcon: BatteryAlertIcon, chargingIcon: BatteryCharging20Icon, color: "secondary" },
    { max: 25, stdIcon: Battery20Icon, chargingIcon: BatteryCharging20Icon, color: color ?? "action" },
    { max: 35, stdIcon: Battery30Icon, chargingIcon: BatteryCharging30Icon, color: color ?? "action" },
    { max: 55, stdIcon: Battery50Icon, chargingIcon: BatteryCharging50Icon, color: color ?? "action" },
    { max: 70, stdIcon: Battery60Icon, chargingIcon: BatteryCharging60Icon, color: color ?? "action" },
    { max: 85, stdIcon: Battery80Icon, chargingIcon: BatteryCharging80Icon, color: color ?? "action" },
    { max: 95, stdIcon: Battery90Icon, chargingIcon: BatteryCharging90Icon, color: color ?? "action" },
    { max: 100, stdIcon: BatteryFullIcon, chargingIcon: BatteryChargingFullIcon, color: color ?? "action" },
  ];

  const match = steps.find((s) => p <= s.max);
  if (!match) {
    return <BatteryUnknownIcon color="action" />;
  }

  const Icon = isCharging ? match.chargingIcon : match.stdIcon;

  // need color prop and sx color to apply "action", "secondary" or custom color correctly
  return <Icon color={match.color} sx={{ color: match.color }} />;
};

export default BatteryIcon;
